import React from "react";
import {
  ResponsiveContainer,
  BarChart as ReBarChart,
  Bar,
  Tooltip,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import { Inbox } from "lucide-react";
import { useTheme } from "src/hooks/useTheme";

interface BarChartProps {
  data: any[];
  dataKey: string;
  xKey: string;
  containerClassName?: string;
  barColor?: string;
  barRadius?: [number, number, number, number];
  hideXAxis?: boolean;
  hideYAxis?: boolean;
  xAxisProps?: Record<string, any>;
  yAxisProps?: Record<string, any>;
  tooltipFormatter?: (value?: number) => string | [string, string];
  tooltipLabelFormatter?: (label?: string) => string;
  xTickFormatter?: (value: string) => string;
  rotateXAxisLabels?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  dataKey,
  xKey,
  containerClassName = "mt-4 h-44 w-full",
  barColor = "#A5B4FC",
  barRadius = [8, 8, 8, 8],
  hideXAxis = false,
  hideYAxis = false,
  xAxisProps,
  yAxisProps,
  tooltipFormatter,
  tooltipLabelFormatter,
  xTickFormatter,
  rotateXAxisLabels = false,
  isEmpty = false,
  emptyMessage,
}) => {
  const xAxisReservedHeight = rotateXAxisLabels ? 36 : 0;
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const gridStroke = isDark ? "#374151" : "#E5E7EB";

  return (
    <div className={`relative ${containerClassName}`}>
      <ResponsiveContainer width="100%" height="100%">
        <ReBarChart
          data={data}
          margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0,
          }}
        >
          <CartesianGrid stroke={gridStroke} vertical={false} />
          <XAxis
            dataKey={xKey}
            hide={hideXAxis}
            tickFormatter={xTickFormatter}
            tick={{ fontSize: 11, fill: "#9CA3AF" }}
            tickLine={false}
            axisLine={{ stroke: gridStroke }}
            {...(rotateXAxisLabels
              ? { angle: -45, textAnchor: "end", height: xAxisReservedHeight }
              : {})}
            {...xAxisProps}
          />
          <YAxis hide={hideYAxis} {...yAxisProps} />
          <Tooltip
            cursor={{ fill: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }}
            contentStyle={{
              borderRadius: "8px",
              borderColor: isDark ? "#374151" : "#E5E7EB",
              backgroundColor: isDark ? "#1F2937" : "#FFFFFF",
              color: isDark ? "#E5E7EB" : "#111827",
              fontSize: "12px",
            }}
            labelStyle={{ fontWeight: 600, color: isDark ? "#F3F4F6" : "#111827" }}
            itemStyle={{ color: isDark ? "#E5E7EB" : "#111827" }}
            formatter={tooltipFormatter}
            labelFormatter={tooltipLabelFormatter}
          />
          {!isEmpty && (
            <Bar dataKey={dataKey} fill={barColor} radius={barRadius} />
          )}
        </ReBarChart>
      </ResponsiveContainer>
      {isEmpty && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 flex flex-col items-center justify-center gap-2"
          style={{ bottom: xAxisReservedHeight }}
        >
          <div className="rounded-full bg-gray-50 p-2.5 dark:bg-gray-700">
            <Inbox size={18} className="text-gray-300 dark:text-gray-400" />
          </div>
          {emptyMessage && (
            <span className="text-xs font-medium text-gray-400 dark:text-gray-400">
              {emptyMessage}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default BarChart;
